import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import {
  GuardianLinkEndReasonSchema,
  GuardianLinkStatusSchema,
} from '../guardian-link-status/guardian-link-status';
import { GuardianLabelSchema } from '../guardian-label/guardian-label';

/**
 * A guardian, as his ward reads him.
 *
 * The counterpart of {@link WardLinkSchema}, and **deliberately not the same
 * schema with a renamed field**. The two sides of a link want different facts:
 * she is owed the label *she* wrote and the ability to revoke; he is owed the
 * label *he* wrote. Deriving one from the other through a shared base is how a
 * field added for one audience arrives in the other's payload without anybody
 * choosing it — the trap P22b4 recorded when a base schema's new column reached
 * every contract built from it. Two small schemas, no inheritance, no surprises.
 *
 * **No profile data, and no route to any.** A guardian is identified to his ward
 * by his user id and by the name she gave him — never by his own answers, which
 * would make this feature a second composer of member-facing payloads beside
 * `ProfileVisibilityResolver` (N18).
 */
export const GuardianLinkSchema = z
  .object({
    id: UuidSchema,
    guardianUserId: UuidSchema,
    /** What she called him when she issued the code, snapshotted onto the link. */
    guardianLabel: GuardianLabelSchema,
    status: GuardianLinkStatusSchema,
    linkedAt: z.iso.datetime(),
    /** When his access stopped, or `null` while it has not. */
    endedAt: z.iso.datetime().nullable(),
    endedReason: GuardianLinkEndReasonSchema.nullable(),
    createdAt: z.iso.datetime(),
  })
  .strict()
  .refine((link) => (link.status === 'ended') === (link.endedAt !== null), {
    message: 'An ended link records when it ended, and a live one records nothing.',
    path: ['endedAt'],
  });

export type GuardianLink = z.infer<typeof GuardianLinkSchema>;
